import { ApiError } from '../../lib/api';

/**
 * The one approve failure that is a PROTOCOL rather than a dead end.
 *
 * `POST /admin/documents/:id/approve` answers 409 when the document decision
 * landed — it is flipped to `verified` and deliberately NOT rolled back — but
 * the KYC state machine refused to promote the client to identity-verified
 * (another document expired, risk score over the line…). See
 * `apps/api/src/modules/admin-documents/admin-documents.service.ts`.
 *
 * The body is exactly the object handed to `ConflictException`, because Nest
 * passes a non-string payload through untouched:
 *
 * ```json
 * {
 *   "code": "KYC_VERDICT_NOT_APPLIED",
 *   "error": "The document was marked verified, but the client could not be…",
 *   "documentId": "…",
 *   "userId": "…",
 *   "kycCode": "…"
 * }
 * ```
 *
 * Treating it as a plain failure is what makes the screen lie twice: the
 * operator is told the approval did nothing, while the queue keeps drawing a
 * document the server already decided. Callers must re-read and say which half
 * succeeded.
 */
export const KYC_VERDICT_NOT_APPLIED = 'KYC_VERDICT_NOT_APPLIED';

/** What the 409 carries that is worth putting in front of an operator. */
export interface KycVerdictNotApplied {
  /** The state machine's own refusal code, when it named one. */
  readonly kycCode?: string;
  readonly documentId?: string;
}

/**
 * Recognise the 409 above, or `undefined` for every other error — including a
 * 409 from some other endpoint protocol, which is why the `code` is checked
 * and not just the status.
 */
export function kycVerdictNotApplied(error: unknown): KycVerdictNotApplied | undefined {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined;
  if (typeof error.body !== 'object' || error.body === null) return undefined;

  const body = error.body as Record<string, unknown>;
  if (body.code !== KYC_VERDICT_NOT_APPLIED) return undefined;

  return {
    kycCode: typeof body.kycCode === 'string' && body.kycCode !== '' ? body.kycCode : undefined,
    documentId: typeof body.documentId === 'string' ? body.documentId : undefined,
  };
}

/**
 * The operator-facing sentence for {@link kycVerdictNotApplied}: the localised
 * explanation plus the machine code the state machine refused with, so a
 * support ticket carries something a developer can grep for. Kept out of the
 * i18n string itself — `t()` echoes the key back when interpolation misses,
 * and the code is frequently absent.
 */
export function kycVerdictNotAppliedMessage(
  detail: KycVerdictNotApplied,
  localised: string,
): string {
  return detail.kycCode ? `${localised} (${detail.kycCode})` : localised;
}

/**
 * What to put in the toast for any OTHER approve failure. The API's own
 * sentence says more than «Не вдалося підтвердити документ.»; a network
 * failure or a bare status carries nothing, and keeps the generic string.
 */
export function approveErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;

  const detail = error.message.trim();
  if (detail === '' || /^HTTP \d+$/.test(detail)) return fallback;

  return `${fallback} ${detail}`;
}
